/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Injectable } from '@angular/core';

import { ConnectCallback, PostMessagePayload, startListening } from 'postmessage-promise';
import { EventManagerService } from '../event-manager/event-manager.service';


export enum PostType {
  // other messages
  resetSession = 'resetSession',
  confetti = 'confetti',
  addStat = 'addStat',

  // normal messages
  runMessage = 'runMessage', // deliver chatmessage to open ai
  runComplete = 'runComplete', // recieved chatmessage and post to iframe parent

  // bypass messages
  addMessage = 'addMessage', // bypass message to open ai as user
  userBypassComplete = "userBypassComplete", // bypass message to open ai as user completed
  addMessageBot = 'addMessageBot', // bypass message to open ai as bot
  botBypassComplete = "botBypassComplete", // bypass message to open ai as bot completed

  // simulate messages
  botMessage = 'botMessage', // send message as bot
  botMessageComplete = "botMessageComplete", // recieved message from iframe as bot
  userMessage = 'userMessage', // send message as user

  // audio player
  botSayMessage = "botSayMessage", // say message as bot
  botSayLoad = "botSayLoad", // bot is generating mp3 file
  botSayLoadComplete = "botSayLoadComplete", // bot has generated mp3 file
  botSayEnded = "botSayEnded", // mp3 file has played

  // card messages
  botcard = "botcard",
  scormMessage = "scormMessage",
  scormHook = "scormHook",
  callExternalFunction = "callExternalFunction",
  getRegisteredFunctionNames = "getRegisteredFunctionNames",
  registerCourse = "registerCourse",
  waitForInitialIdle = "waitForInitialIdle",
  waitForNextIdle = "waitForNextIdle"
}

export enum ScormPayloadType {
  LMSSetValue = "LMSSetValue",
  LMSGetValue = "LMSGetValue",
  LMSFinish = "LMSFinish",
  LMSIsInitialized = "LMSIsInitialized",
  LMSCommit = "LMSCommit",
  LMSInitialize = "LMSInitialize",
  LMSGetLastError = "LMSGetLastError",
  LMSGetErrorString = "LMSGetErrorString",
  LMSGetDiagnostic = "LMSGetDiagnostic"
}

export type MessageData<PayloadType = unknown> = {
  type: PostType
  payload: PayloadType
}


/**
 * Brückt RAGAI-Aktionen per `postMessage` zur einbettenden Elternseite (`RAGAI.BOTAPI`), z. B. für das embed.js-SDK.
 * @remarks
 * Nutzt die `postmessage-promise`-Bibliothek für ein Request/Response-Protokoll über `window.postMessage` -
 * jede Nachricht hat einen `type` aus dem {@link PostType}-Enum, das gleichzeitig als Vertrag zwischen Bot-Iframe
 * und Elternseite dient (z. B. `runComplete` postet abgeschlossene Bot-Antworten nach außen, `botSayLoad`/
 * `botSayLoadComplete`/`botSayEnded` bilden den Lebenszyklus einer Sprachausgabe ab). Wird u. a. von mehreren
 * anderen RAGAI-Modulen intern genutzt (z. B. `RAGAI.AUDIO`, `RAGAI.MSG`), um Zustandsänderungen nach außen zu melden.
 */
@Injectable({
  providedIn: 'root'
})
export class BotapiManagerService {
  private _postMessage?: ConnectCallback["postMessage"];
  private _listenMessage?: ConnectCallback["listenMessage"];
  private _destroy?: ConnectCallback["destroy"];


  constructor(
    private eventManager: EventManagerService,
  ) {
    void this.init();
  }

  /** Type-Cast-Helfer für rohe postMessage-Payloads (keine Laufzeitprüfung). */
  public typePlayload<T>(payload: unknown): T {
    return payload as T;
  }

  /** Startet das postMessage-Protokoll zur Elternseite und registriert die Handler für alle `PostType`-Nachrichten. */
  public async init() {

    console.debug("init iframe communication startListening");
    const e = await startListening({ timeout: 60000 });
    console.debug("init iframe communication startListening done");
    this._postMessage = e.postMessage;
    this._listenMessage = e.listenMessage;
    this._destroy = e.destroy;
    console.log("init iframe communication");

    this.listenMessage(async (method: string, payload, response) => {

      switch (method as PostType) {
        case PostType.confetti:
          await window.RAGAI.confetti({});
          response("Habe party gemacht");
          break;
        case PostType.userMessage:
          window.RAGAI.userMessage(payload as string);
          response("ok");
          break;
        case PostType.runMessage:
          window.RAGAI.runMessage(payload as string);
          response("ok");
          break;
        case PostType.addMessage: // admessage byoass to open ai
          response(await window.RAGAI.addMessage(payload as string));
          break;
        case PostType.addMessageBot: // admessage byoass to open ai
          response(await window.RAGAI.addMessageBot(payload as string));
          break;
        case PostType.botMessage: // simulate the bot
          void window.RAGAI.botMessage(payload as string);
          response("ok");
          break;
        case PostType.botSayMessage:
          const { text, voice } = this.typePlayload<{ text: string, voice: string }>(payload);
          response(await window.RAGAI.AUDIO.botSay(text, voice));
          break;
        case PostType.botcard:
          const { data, type } = this.typePlayload<{ type: string, data: unknown }>(payload);
          response(await window.RAGAI.addBotCard({ data, type }));
          break;
        case PostType.resetSession:
          await window.RAGAI.resetBot();
          response("ok");
          break;

        case PostType.scormHook:
          const { hook, params } = this.typePlayload<{ hook: string, params: unknown }>(payload);
          window.RAGAI.SCORM.scormHook(hook, params);
          break;
        case PostType.waitForInitialIdle:
          await window.RAGAI.WAITFOR.initialIdle();
          response("ok");
          break;
        case PostType.waitForNextIdle:
          await window.RAGAI.WAITFOR.nextIdle();
          response("ok");
          break;
        default:
          console.log("Unknown message type " + method, payload);
      }
    });



    try {
      // remove THAT code when all deployed scorm bot are newly exported an deployed to lms
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      window.parent.API;
    } catch (e) {
      console.warn("USING legacy scorm");
      window.RAGAI.SCORM.LMSSetValue = async (key: string, value: string) => {
        return await this.postMessage(PostType.scormMessage, { type: ScormPayloadType.LMSSetValue, key, value });
      };
      // @ts-ignore
      window.RAGAI.SCORM.LMSGetValue = async (key: string) => {
        const result = await this.postMessage<string | number>(PostType.scormMessage, { type: ScormPayloadType.LMSGetValue, key });
        if (result == undefined) {
          throw new Error("Could not get LMS value. Maybe not initialized");
        } else {
          return result;
        }
      };
    }
    // INITIALER EVENT HOOK
    this.eventManager.addEventHook("eventsToPostMsg", (eventName, args) => {
      void this.postMessage("eventStream", { eventName, args });
    });
  }


  /** Sendet eine postMessage-Nachricht an die Elternseite und wartet auf die Antwort; `undefined` wenn noch kein Frame registriert ist. */
  public async postMessage<T>(method: string, payload: PostMessagePayload) {
    if (this._postMessage == null) {
      // console.debug("No Frame has registered yet for postmessage");
      return;
    }

    return await this._postMessage(method, payload) as Promise<T>;
  }

  public get listenMessage() {
    if (this._listenMessage != null) {
      return this._listenMessage;
    }
    throw new Error("listenMessage is not initialized.");
  }


  /** Ruft eine auf der Elternseite registrierte externe Funktion anhand ihres Namens auf. */
  public async callExternalFunction(name: string, ...args: unknown[]) {
    return await this.postMessage(PostType.callExternalFunction, { name, args });
  }
  /** Liefert die Namen aller auf der Elternseite registrierten externen Funktionen. */
  public async getRegisteredFunctionNames() {
    return await this.postMessage(PostType.getRegisteredFunctionNames, {});
  }
}
